import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Building2, ChevronRight, Users, Mail, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    client_manager_id: "",
    hourly_rate: "", retained_hours: "", billing_cycle_day: "1",
  });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/team")]).then(([c, t]) => {
      setClients(c.data);
      setTeam(t.data);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      toast.success("Client deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete client");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleCreate = async (e) => {    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        address: form.address || null,
        client_manager_id: form.client_manager_id || null,
        billing_config: {
          hourly_rate: parseFloat(form.hourly_rate) || 0,
          retained_hours: parseFloat(form.retained_hours) || 0,
          billing_cycle_day: parseInt(form.billing_cycle_day) || 1,
        },
      };
      const r = await api.post("/clients", payload);
      setClients((prev) => [r.data, ...prev]);
      setShowDialog(false);
      setForm({ name: "", email: "", phone: "", address: "", client_manager_id: "", hourly_rate: "", retained_hours: "", billing_cycle_day: "1" });
      toast.success("Client created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="clients-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Clients</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>
              {clients.length} client organisation{clients.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            data-testid="add-client-button"
            onClick={() => setShowDialog(true)}
            className="flex items-center gap-2"
            style={{ background: "#292524", color: "#fff" }}
          >
            <Plus size={15} strokeWidth={1.5} />
            Add Client
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#A8A29E" }} />
          <input
            data-testid="client-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none"
            style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
          />
        </div>

        {/* List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Building2 size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm" style={{ color: "#A8A29E" }}>No clients found</p>
            </div>
          ) : (
            filtered.map((client) => (
              <div
                key={client.id}
                data-testid={`client-row-${client.id}`}
                onClick={() => navigate(`/clients/${client.id}`)}
                className="bg-white rounded-xl border p-5 flex items-center justify-between cursor-pointer transition-shadow hover:shadow-md group relative"
                style={{ borderColor: "#E7E5E4" }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm overflow-hidden"
                    style={{ background: client.logo_image ? "transparent" : "#D97706" }}
                  >
                    {client.logo_image
                      ? <img src={client.logo_image} alt="" className="w-full h-full object-cover" />
                      : client.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: "#292524" }}>{client.name}</p>
                    <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: "#A8A29E" }}>
                      <Mail size={11} />
                      {client.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {client.billing_config?.hourly_rate > 0 && (
                    <span className="text-sm hidden sm:block" style={{ color: "#57534E" }}>
                      £{client.billing_config.hourly_rate}/hr
                    </span>
                  )}
                  {client.billing_config?.retained_hours > 0 && (
                    <span className="text-xs px-2 py-1 rounded-full hidden sm:block" style={{ background: "#FEF3C7", color: "#D97706" }}>
                      {client.billing_config.retained_hours}h retained
                    </span>
                  )}
                  <div className="flex items-center gap-1 text-xs hidden md:flex" style={{ color: "#A8A29E" }}>
                    <Users size={12} />
                    {client.client_manager_name || "Unassigned"}
                  </div>
                  <button
                    data-testid={`delete-client-${client.id}`}
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(client); }}
                    className="p-1.5 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: "#DC2626" }}
                  >
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={16} style={{ color: "#D6D3D1" }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="create-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">New Client Organisation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Organisation Name *</Label>
                <Input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Ltd" />
              </div>
              <div className="col-span-2">
                <Label>Contact Email *</Label>
                <Input data-testid="client-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="hello@client.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44 000 000 0000" />
              </div>
              <div>
                <Label>Client Manager</Label>
                <select
                  value={form.client_manager_id}
                  onChange={(e) => setForm({ ...form, client_manager_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
                >
                  <option value="">Select manager</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="border-t pt-4" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "#A8A29E" }}>Billing Configuration</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Hourly Rate (£)</Label>
                  <Input data-testid="client-hourly-rate-input" type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="150.00" />
                </div>
                <div>
                  <Label>Retained Hours/mo</Label>
                  <Input type="number" min="0" step="0.5" value={form.retained_hours} onChange={(e) => setForm({ ...form, retained_hours: e.target.value })} placeholder="10" />
                </div>
                <div>
                  <Label>Billing Cycle Day</Label>
                  <Input type="number" min="1" max="28" value={form.billing_cycle_day} onChange={(e) => setForm({ ...form, billing_cycle_day: e.target.value })} placeholder="1" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="create-client-submit" type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>
                {saving ? "Creating..." : "Create Client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This will permanently remove all their users, tasks, invoices, messages, and billing data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-client-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-client-confirm"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
